import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import KPICard from '../../components/primitives/KPICard';
import Panel from '../../components/primitives/Panel';
import StatusPill from '../../components/primitives/StatusPill';
import { PGBarChart, PGPieChart } from '../../components/primitives/PGChart';
import {
  FLEET_GROUP,
  FLEET_BRANCH,
  VEHICLES,
  complianceWatchlist,
  fmtRk,
  fmtKm,
} from '../../utils/fleetData';
import { BRANCHES } from '../../utils/orgData';
import { PG } from '../../utils/theme';

const {
  FiTruck, FiDroplet, FiTool, FiDollarSign, FiActivity,
  FiAlertTriangle, FiZap, FiNavigation, FiPercent, FiChevronRight,
} = FiIcons;

const costKmColor = (v) => (v <= 5 ? PG.emerald : v <= 6.5 ? PG.amber : PG.red);
const activePctColor = (v) => (v < 70 ? PG.red : v < 85 ? PG.amber : PG.emerald);
const revVehicleColor = (v) => (v >= 160000 ? PG.emerald : v >= 120000 ? PG.amber : PG.red);

const COLS = [
  { key: 'name',          label: 'Branch',     align: 'left',  sortable: true },
  { key: 'region',        label: 'Region',     align: 'left',  sortable: true },
  { key: 'size',          label: 'Fleet',      align: 'right', sortable: true },
  { key: 'activePct',     label: 'Active%',    align: 'right', sortable: true },
  { key: 'kmMTD',         label: 'Km_MTD',     align: 'right', sortable: true },
  { key: 'fuelMTD',       label: 'Fuel_R',     align: 'right', sortable: true },
  { key: 'maintMTD',      label: 'Maint_R',    align: 'right', sortable: true },
  { key: 'revPerVehicle', label: 'Rev/Veh',    align: 'right', sortable: true },
  { key: 'costPerKm',     label: 'Cost/Km',    align: 'right', sortable: true },
];

export default function FleetGroupView() {
  const g = FLEET_GROUP;

  const [sortKey, setSortKey] = useState('revPerVehicle');
  const [sortDir, setSortDir] = useState('desc');

  const rows = useMemo(() => {
    const base = BRANCHES.map((b) => ({
      branchId: b.id,
      name: b.name,
      region: b.region,
      country: b.country,
      ...FLEET_BRANCH[b.id],
    }));
    const sorted = [...base].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === 'string') {
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return sorted;
  }, [sortKey, sortDir]);

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' || key === 'region' ? 'asc' : 'desc');
    }
  };

  const watchlist = useMemo(() => complianceWatchlist(VEHICLES, 60), []);
  const watchlistTop = watchlist.slice(0, 8);
  const hasAlert = watchlist.some((v) => v.flags.some((f) => f.tone === 'alert'));

  const typeMixData = g.typeMix
    .filter((t) => t.count > 0)
    .map((t) => ({ name: t.name, value: t.count }));

  const revRanked = [...BRANCHES]
    .map((b) => ({ name: b.name, rev: FLEET_BRANCH[b.id].revPerVehicle }))
    .sort((a, b) => b.rev - a.rev)
    .slice(0, 10);

  const costKmRanked = [...BRANCHES]
    .map((b) => ({ name: b.name, cpk: FLEET_BRANCH[b.id].costPerKm }))
    .sort((a, b) => a.cpk - b.cpk);

  const statusTiles = [
    { label: 'Active',   value: g.active,   total: g.size, color: PG.emerald, tone: 'ok' },
    { label: 'Service',  value: g.service,  total: g.size, color: PG.amber,   tone: 'warn' },
    { label: 'Standby',  value: g.standby,  total: g.size, color: PG.cyan,    tone: 'info' },
    { label: 'Grounded', value: g.grounded, total: g.size, color: PG.red,     tone: 'alert' },
  ];

  const kmMtdK = (g.kmMTD / 1000).toFixed(0) + 'k';
  const fuelBudgetK = Math.round(g.fuelMTD * 1.1 / 1000) + 'k';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col gap-4 min-h-0"
    >
      {/* KPI strip */}
      <div className="grid grid-cols-8 gap-3 flex-shrink-0">
        <KPICard
          title="Fleet_Size"
          value={g.size}
          unit="vehicles"
          icon={FiTruck}
          accent={PG.cyan}
          trend="up"
          trendValue="+3 YTD"
        />
        <KPICard
          title="Active_Today"
          value={g.active}
          unit={`of ${g.size}`}
          target={`${g.activePct}%`}
          icon={FiActivity}
          accent={PG.emerald}
          trend={g.activePct >= 85 ? 'up' : 'down'}
          trendValue={`${g.activePct}%`}
        />
        <KPICard
          title="Grounded"
          value={g.grounded}
          unit="units"
          target="0"
          icon={FiAlertTriangle}
          accent={g.grounded > 0 ? PG.red : PG.emerald}
          trend={g.grounded > 0 ? 'down' : 'up'}
          trendValue={g.grounded > 0 ? `${g.grounded} down` : 'Clean'}
        />
        <KPICard
          title="Km_MTD"
          value={kmMtdK}
          unit="km"
          icon={FiNavigation}
          accent={PG.violet}
          trend="up"
          trendValue="+4.2%"
        />
        <KPICard
          title="Fuel_Cost_MTD"
          value={fmtRk(g.fuelMTD)}
          target={`R ${fuelBudgetK} budget`}
          icon={FiDroplet}
          accent={PG.amber}
          trend="flat"
          trendValue="On plan"
        />
        <KPICard
          title="Maint_Cost_MTD"
          value={fmtRk(g.maintMTD)}
          icon={FiTool}
          accent={g.maintMTD > 150000 ? PG.red : PG.amber}
          trend={g.maintMTD > 150000 ? 'down' : 'up'}
          trendValue={g.maintMTD > 150000 ? 'Over' : 'Under'}
        />
        <KPICard
          title="Revenue_/_Vehicle"
          value={fmtRk(g.revPerVehicle)}
          icon={FiDollarSign}
          accent={PG.emerald}
          trend="up"
          trendValue="+2.8%"
        />
        <KPICard
          title="Cost_/_Km"
          value={`R ${g.costPerKm.toFixed(2)}`}
          target="≤ R 5.50"
          icon={FiPercent}
          accent={g.costPerKm > 5.5 ? PG.red : PG.emerald}
          trend={g.costPerKm > 5.5 ? 'down' : 'up'}
          trendValue={g.costPerKm > 5.5 ? 'Over' : 'Under'}
        />
      </div>

      {/* Body grid */}
      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0">
        {/* Left column */}
        <div className="col-span-5 flex flex-col gap-4 min-h-0">
          <Panel
            title="Fleet_Status_Breakdown"
            subtitle={`${g.size} vehicles // live status`}
            className="flex-shrink-0"
          >
            <div className="grid grid-cols-4 gap-2 p-3">
              {statusTiles.map((t) => {
                const pct = t.total > 0 ? (t.value / t.total) * 100 : 0;
                return (
                  <div
                    key={t.label}
                    className="flex flex-col justify-between p-3 border border-white/10 bg-black"
                    style={{ borderColor: `${t.color}33` }}
                  >
                    <div
                      className="font-doto text-3xl font-black leading-none"
                      style={{ color: t.color }}
                    >
                      {t.value}
                    </div>
                    <div>
                      <div className="font-mono-tech text-[9px] uppercase tracking-[0.2em] text-neutral-500 mt-2">
                        {t.label}
                      </div>
                      <div className="mt-2 h-1 bg-white/5">
                        <div
                          className="h-full"
                          style={{ width: `${pct}%`, backgroundColor: t.color }}
                        />
                      </div>
                      <div className="font-mono-tech text-[9px] tracking-widest text-neutral-600 mt-1">
                        {pct.toFixed(0)}%
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Panel>

          <Panel
            title="Vehicle_Type_Mix"
            subtitle="count by type"
            className="flex-1 min-h-0"
          >
            <div className="h-full p-2">
              <PGPieChart data={typeMixData} />
            </div>
          </Panel>

          <Panel
            title="Compliance_Watchlist"
            subtitle="licence / insurance / service / incidents"
            right={
              <StatusPill tone={hasAlert ? 'alert' : 'warn'}>
                {watchlist.length}
              </StatusPill>
            }
            className="flex-1 min-h-0"
          >
            <div className="h-full overflow-y-auto custom-scrollbar">
              <div className="p-2 space-y-1">
                {watchlistTop.map((v) => (
                  <div
                    key={v.id}
                    onClick={() => { window.location.hash = `#/sliver/ops/branch/${v.branchId}`; }}
                    className="flex items-center justify-between px-3 py-2 border border-white/10 hover:border-[#D71921] hover:bg-[#D71921]/5 transition-all cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <FiZap className="text-[#FFB800] flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="font-mono-tech text-[11px] uppercase tracking-wider text-white truncate">
                          {v.registration}
                        </div>
                        <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-600 truncate">
                          {v.branchName} // {v.typeName}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {v.flags.slice(0, 2).map((f, i) => (
                        <StatusPill key={i} tone={f.tone}>{f.label}</StatusPill>
                      ))}
                      <FiChevronRight className="text-neutral-600" />
                    </div>
                  </div>
                ))}
                {watchlistTop.length === 0 && (
                  <div className="p-6 text-center font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">
                    All clear
                  </div>
                )}
              </div>
            </div>
          </Panel>
        </div>

        {/* Right column */}
        <div className="col-span-7 flex flex-col gap-4 min-h-0">
          <Panel
            title="Branch_Fleet_Comparison"
            subtitle={`${BRANCHES.length} branches // click to drill`}
            right={<StatusPill tone="info">Live</StatusPill>}
            className="flex-1 min-h-0"
          >
            <div className="h-full overflow-y-auto custom-scrollbar">
              <table className="w-full text-left">
                <thead className="sticky top-0 bg-[#0A0A0A] z-10">
                  <tr className="border-b border-white/10">
                    {COLS.map((c) => (
                      <th
                        key={c.key}
                        onClick={() => c.sortable && toggleSort(c.key)}
                        className={`px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 ${
                          c.align === 'right' ? 'text-right' : 'text-left'
                        } ${c.sortable ? 'cursor-pointer hover:text-white' : ''}`}
                      >
                        {c.label}
                        {c.sortable && sortKey === c.key && (
                          <span className="ml-1 text-neutral-400">
                            {sortDir === 'asc' ? '▲' : '▼'}
                          </span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.branchId}
                      className="row-clickable border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                      onClick={() => { window.location.hash = `#/sliver/ops/branch/${r.branchId}`; }}
                    >
                      <td className="px-3 py-2">
                        <div className="font-mono-tech text-[11px] uppercase tracking-wider text-white">
                          {r.name}
                        </div>
                        <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-600">
                          {r.country}
                        </div>
                      </td>
                      <td className="px-3 py-2 font-mono-tech text-[10px] uppercase tracking-wider text-neutral-400">
                        {r.region}
                      </td>
                      <td className="px-3 py-2 text-right font-doto text-base text-white">{r.size}</td>
                      <td className="px-3 py-2 text-right">
                        <span
                          className="font-doto text-base"
                          style={{ color: activePctColor(r.activePct) }}
                        >
                          {r.activePct}%
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono-tech text-[11px] text-neutral-300">
                        {fmtKm(r.kmMTD)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-tech text-[11px] text-neutral-300">
                        {fmtRk(r.fuelMTD)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-tech text-[11px] text-neutral-300">
                        {fmtRk(r.maintMTD)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-tech text-[11px] text-white">
                        {fmtRk(r.revPerVehicle)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span
                          className="font-doto text-base"
                          style={{ color: costKmColor(r.costPerKm) }}
                        >
                          {r.costPerKm.toFixed(2)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <div className="grid grid-cols-2 gap-4 flex-shrink-0">
            <Panel
              title="Revenue_Per_Vehicle_By_Branch"
              subtitle="top 10"
              className="h-56"
            >
              <div className="h-full p-2">
                <PGBarChart
                  horizontal
                  categories={revRanked.map((r) => r.name).reverse()}
                  series={[{
                    name: 'Rev / vehicle',
                    data: revRanked
                      .map((r) => ({
                        value: r.rev,
                        itemStyle: { color: revVehicleColor(r.rev) },
                      }))
                      .reverse(),
                  }]}
                  yFormatter={(v) => 'R ' + (v / 1000).toFixed(0) + 'k'}
                />
              </div>
            </Panel>

            <Panel
              title="Cost_/_Km_By_Branch"
              subtitle="cheapest first"
              className="h-56"
            >
              <div className="h-full p-2">
                <PGBarChart
                  horizontal
                  categories={costKmRanked.map((r) => r.name).reverse()}
                  series={[{
                    name: 'Cost / km',
                    data: costKmRanked
                      .map((r) => ({
                        value: r.cpk,
                        itemStyle: { color: costKmColor(r.cpk) },
                      }))
                      .reverse(),
                  }]}
                  yFormatter={(v) => 'R ' + v.toFixed(1)}
                />
              </div>
            </Panel>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
